import React, { useMemo, useState } from 'react';
import {
  Box, Stack, TextField, Typography, Chip, LinearProgress,
  IconButton, InputAdornment, Alert, Divider
} from '@mui/material';
import { Visibility, VisibilityOff, Key } from '@mui/icons-material';
import API from '../../services/api';
import ButtonSubmit from '../../components/ButtonSubmit';

export default function ChangePasswordForm({
  setOpen,
  changePwdPath = 'accounts/users/change_password/',
  policy = { minLen: 12 },
}) {
  const [pw, setPw] = useState({ old_password: '', new_password: '', confirm: '' });
  const [show, setShow] = useState({ old: false, n: false, c: false });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const score = useMemo(() => {
    const v = pw.new_password || '';
    let s = 0;
    if (v.length >= (policy.minLen || 12)) s += 25;
    if (/[a-z]/.test(v) && /[A-Z]/.test(v)) s += 25;
    if (/\d/.test(v)) s += 25;
    if (/[^A-Za-z0-9]/.test(v)) s += 25;
    return s; // 0..100
  }, [pw.new_password, policy.minLen]);

  const strength = useMemo(() => {
    if (score >= 90) return { label: 'Muy segura', color: 'success.main' };
    if (score >= 70) return { label: 'Segura', color: 'success.light' };
    if (score >= 40) return { label: 'Media', color: 'warning.main' };
    if (score > 0)  return { label: 'Débil', color: 'error.main' };
    return { label: '—', color: 'text.disabled' };
  }, [score]);

  const confirmError = pw.confirm && pw.confirm !== pw.new_password ? 'No coincide con la nueva contraseña' : '';

  const canSubmit =
    pw.old_password &&
    pw.new_password &&
    pw.confirm &&
    !confirmError &&
    score >= 40; // mínimo "Media"

  const onChange = (field) => (e) => setPw((s) => ({ ...s, [field]: e.target.value }));

  const submit = async () => {
    setMsg({ type: '', text: '' });
    if (!canSubmit) {
      setMsg({ type: 'error', text: 'Revisa los campos y cumple los requisitos.' });
      return;
    }
    try {
      setLoading(true);
      await API.post(changePwdPath, {
        old_password: pw.old_password,
        new_password: pw.new_password,
      });
      setMsg({ type: 'success', text: 'Contraseña cambiada correctamente.' });
      setPw({ old_password: '', new_password: '', confirm: '' });
      // Si prefieres cerrar automáticamente:
      // setOpen && setOpen(false);
    } catch {
      setMsg({ type: 'error', text: 'No se pudo cambiar la contraseña.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* título pequeño para no saturar */}
      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
        Cambiar contraseña
      </Typography>

      {/* Fila 1: actual (ancho completo) */}
      <TextField
        label="Contraseña actual"
        type={show.old ? 'text' : 'password'}
        value={pw.old_password}
        onChange={onChange('old_password')}
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShow(s => ({ ...s, old: !s.old }))} edge="end">
                {show.old ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Fila 2: nueva + confirmar (lado a lado en sm+) */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          label="Nueva contraseña"
          type={show.n ? 'text' : 'password'}
          value={pw.new_password}
          onChange={onChange('new_password')}
          size="small"
          fullWidth
          helperText={strength.label}
          FormHelperTextProps={{ sx: { color: strength.color, fontWeight: 600 } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShow(s => ({ ...s, n: !s.n }))} edge="end">
                  {show.n ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirmar"
          type={show.c ? 'text' : 'password'}
          value={pw.confirm}
          onChange={onChange('confirm')}
          size="small"
          fullWidth
          error={Boolean(confirmError)}
          helperText={confirmError || ' '}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShow(s => ({ ...s, c: !s.c }))} edge="end">
                  {show.c ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Barra de fuerza + requisitos con aire */}
      <Box sx={{ mt: 0.5 }}>
        <LinearProgress
          variant="determinate"
          value={score}
          sx={{
            height: 8,
            borderRadius: 99,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              borderRadius: 99,
              backgroundColor: strength.color,
            },
          }}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`Mín. ${policy.minLen || 12} caracteres`} variant="outlined" />
          <Chip size="small" label="Mayúsculas y minúsculas" variant="outlined" />
          <Chip size="small" label="Número" variant="outlined" />
          <Chip size="small" label="Símbolo" variant="outlined" />
        </Stack>
      </Box>

      {msg.text && (
        <Alert severity={msg.type === 'success' ? 'success' : 'error'} variant="outlined" sx={{ borderRadius: 2 }}>
          {msg.text}
        </Alert>
      )}

      <Divider />

      {/* Botón principal (tu componente) */}
      <ButtonSubmit
        name="Cambiar contraseña"
        icon={Key}
        loading={loading}
        color="primary"
        onClick={submit}
        disabled={!canSubmit}
        fullWidth
      />

      {/* Acción secundaria */}
      <Stack direction="row" justifyContent="flex-end">
        <Typography
          component="button"
          onClick={() => setOpen && setOpen(false)}
          disabled={loading}
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
        >
          Cerrar
        </Typography>
      </Stack>
    </Box>
  );
}
